import React, { useState, useEffect, useRef } from 'react';
import { Box, TextField, Typography, Chip } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';
import SpeedIcon from '@mui/icons-material/Speed';
import { XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar, ReferenceLine } from 'recharts';
import type { RouteData } from '../../types';
import './RealisticPowerCalculator.css';

// Debug flag - set to false to disable debug logging
const DEBUG = false;

// Helper function to get CSS variable values
const getCSSVariable = (variable: string): string => {
  const root = document.documentElement;
  return getComputedStyle(root).getPropertyValue(variable).trim();
};

// Chart color constants using CSS variables
const CHART_COLORS = {
  grid: '#e0e0e0',      // Fallback for --color-chart-grid
  axis: '#cccccc',      // Fallback for --color-chart-axis  
  text: '#6b7280',      // Fallback for --color-medium-gray
  primary: '#1e4d40'    // Fallback for --color-accent
};

// Function to get actual CSS variable values or fallbacks
const getChartColors = () => {
  try {
    return {
      grid: getCSSVariable('--color-chart-grid') || CHART_COLORS.grid,
      axis: getCSSVariable('--color-chart-axis') || CHART_COLORS.axis,
      text: getCSSVariable('--color-medium-gray') || CHART_COLORS.text,
      primary: getCSSVariable('--color-accent') || CHART_COLORS.primary
    };
  } catch {
    return CHART_COLORS;
  }
};

// Interface for calculator state that gets passed to parent
export interface PowerCalculatorState {
  duration: number | null;      // Calculated duration (null = use default/Auto mode)
  powerToWeight: number;         // Current W/kg setting
  showOriginal: boolean;         // Auto mode enabled/disabled
  averageSpeed: number | null;   // Calculated average speed
  speedChartData: any[];         // Chart data for speed visualization
}

interface RealisticPowerCalculatorProps {
  routeData: RouteData;
  onDurationCalculated?: (duration: number | null) => void; // Legacy callback - deprecated, use onStateChange
  onStateChange?: (state: PowerCalculatorState) => void;     // New callback that reports all calculator state
  displayOnly?: boolean; // If true, component only displays values without managing state or calling callbacks
  headless?: boolean; // If true, component runs calculations but renders nothing (avoids chart warnings when hidden)
  // Controlled mode props (optional - if provided, component uses these instead of internal state)
  controlledPowerToWeight?: number;
  controlledShowOriginal?: boolean;
}

// Bike profile configurations
const BIKE_PROFILES = {
  road: {
    cda: 0.30,           // Coefficient of drag × frontal area (m²) - aerodynamic road position
    rollingCoeff: 0.004, // Base rolling coefficient for road tires
    weight: 8,           // Bike weight (kg)
    efficiency: 0.97     // Drivetrain efficiency
  },
  gravel: {
    cda: 0.35,           // More upright position
    rollingCoeff: 0.006, // Wider tires, lower pressure
    weight: 10,          // Heavier bike
    efficiency: 0.96     // Slightly lower efficiency
  },
  mtb: {
    cda: 0.40,           // Upright position
    rollingCoeff: 0.008, // Knobby tires
    weight: 13,          // Heavy bike
    efficiency: 0.95     // Mountain bike drivetrain
  },
  bike: {
    cda: 0.32,           // Hybrid/touring position
    rollingCoeff: 0.005, // Touring tires
    weight: 12,          // Touring bike
    efficiency: 0.96     // Standard efficiency
  }
};

// Surface adjustments for rolling resistance
const SURFACE_ADJUSTMENTS: Record<string, number> = {
  'paved': 1.0,
  'asphalt': 1.0,
  'concrete': 1.05,
  'gravel': 1.8,
  'unpaved': 2.2,
  'dirt': 2.0,
  'grass': 3.0,
  'sand': 4.0,
  'unknown': 1.3
};

// Calibration system for real-world accuracy - significantly reduced for realism
const CALIBRATION_CONFIG = {
  // Base speed multiplier - much more conservative
  speedMultiplier: 0.98,  // Actually 2% slower than pure physics for safety
  
  // Power efficiency adjustments by W/kg level - minimal bonuses
  powerEfficiencyBonus: {
    high: 1.02,   // 3.0+ W/kg riders get minimal efficiency bonus
    medium: 1.01, // 2.5-3.0 W/kg riders get tiny bonus
    low: 1.00     // <2.5 W/kg riders get no bonus
  },
  
  // Terrain-specific adjustments - very minimal
  terrainAdjustments: {
    flat: 1.01,      // Flat terrain gets tiny advantage
    rolling: 1.00,   // Rolling hills - no adjustment
    climbing: 0.98   // Climbing gets slight penalty for real-world conditions
  }
};

export const RealisticPowerCalculator: React.FC<RealisticPowerCalculatorProps> = ({
  routeData,
  onDurationCalculated,
  onStateChange,
  displayOnly = false,
  headless = false,
  controlledPowerToWeight,
  controlledShowOriginal
}) => {
  // Load saved power-to-weight ratio from localStorage, default to 2.0 (unless controlled)
  const [powerToWeight, setPowerToWeight] = useState<number>(() => {
    if (controlledPowerToWeight !== undefined) return controlledPowerToWeight;
    const saved = localStorage.getItem('cyclingbuddy_power_to_weight');
    return saved ? parseFloat(saved) : 2.0;
  });
  // Load saved Auto mode state from localStorage, default to true (unless controlled)
  const [showOriginal, setShowOriginal] = useState<boolean>(() => {
    if (controlledShowOriginal !== undefined) return controlledShowOriginal;
    const saved = localStorage.getItem('cyclingbuddy_duration_auto_mode');
    return saved ? saved === 'true' : true;
  });
  
  // Use controlled values if provided, otherwise use internal state
  const effectivePowerToWeight = controlledPowerToWeight !== undefined ? controlledPowerToWeight : powerToWeight;
  const effectiveShowOriginal = controlledShowOriginal !== undefined ? controlledShowOriginal : showOriginal;
  const [_isCustomMode, setIsCustomMode] = useState<boolean>(false); // Track if custom mode is active
  const [isDragging, setIsDragging] = useState<boolean>(false); // Track if slider is being dragged
  const [tempPowerToWeight, setTempPowerToWeight] = useState<number>(powerToWeight); // Temporary value during dragging
  const [calculatedDuration, setCalculatedDuration] = useState<number | null>(null);
  const [averageSpeed, setAverageSpeed] = useState<number | null>(null);
  const [tempCalculatedDuration, setTempCalculatedDuration] = useState<number | null>(null); // Temporary duration during dragging
  const [tempAverageSpeed, setTempAverageSpeed] = useState<number | null>(null); // Temporary speed during dragging
  const [error, setError] = useState<string>('');
  const [speedChartData, setSpeedChartData] = useState<any[]>([]);
  const [chartAverageSpeed, setChartAverageSpeed] = useState<number | null>(null);
  
  // Track if we're waiting for calculation after drag ends
  const waitingForCalculation = useRef<boolean>(false);

  // Save power-to-weight ratio to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('cyclingbuddy_power_to_weight', powerToWeight.toString());
  }, [powerToWeight]);

  // Save Auto mode state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('cyclingbuddy_duration_auto_mode', showOriginal.toString());
  }, [showOriginal]);

  // Report calculator state to parent component
  // Sends all calculator state via onStateChange callback
  useEffect(() => {
    // Don't send updates in display-only mode
    if (displayOnly) {
      return;
    }
    
    DEBUG && console.log('🔄 Reporting useEffect triggered:', {
      waitingForCalculation: waitingForCalculation.current,
      showOriginal: effectiveShowOriginal,
      tempCalculatedDuration,
      calculatedDuration,
      wouldReport: !waitingForCalculation.current
    });
    
    if (!waitingForCalculation.current) {
      // If Auto mode is ON, duration is null to use BRouter's default duration
      // If Auto mode is OFF, use the calculated duration
      // But if we have a temp duration (from dragging), use that until the real calculation completes
      const durationToReport = effectiveShowOriginal ? null : (tempCalculatedDuration ?? calculatedDuration);
      
      // Call new state change callback with all calculator state
      if (onStateChange) {
        DEBUG && console.log('📤 Sending full state via onStateChange:', {
          duration: durationToReport,
          powerToWeight: effectivePowerToWeight,
          showOriginal: effectiveShowOriginal,
          averageSpeed,
          speedChartData: speedChartData.length
        });
        onStateChange({
          duration: durationToReport,
          powerToWeight: effectivePowerToWeight,
          showOriginal: effectiveShowOriginal,
          averageSpeed,
          speedChartData
        });
      }
      
      // Also call legacy callback for backward compatibility
      if (onDurationCalculated) {
        DEBUG && console.log('📤 Sending duration (legacy):', durationToReport);
        onDurationCalculated(durationToReport);
      }
    }
  }, [calculatedDuration, effectiveShowOriginal, effectivePowerToWeight, averageSpeed, speedChartData, tempCalculatedDuration, onStateChange, onDurationCalculated, displayOnly]);

  // Clear temp values when real calculation completes (after dragging ends)
  useEffect(() => {
    DEBUG && console.log('🧹 Clear temp useEffect:', {
      isDragging,
      tempCalculatedDuration,
      calculatedDuration,
      calculatedDurationType: typeof calculatedDuration
    });
    
    // Only clear if:
    // 1. Not dragging anymore
    // 2. We have a temp value from dragging
    // 3. We have a real calculated value that is a NUMBER (not string)
    // 4. The real calculated value is close to the temp value (within 10%)
    if (!isDragging && 
        tempCalculatedDuration !== null && 
        calculatedDuration !== null && 
        typeof calculatedDuration === 'number') {
      
      const percentDiff = Math.abs((calculatedDuration - tempCalculatedDuration) / tempCalculatedDuration) * 100;
      DEBUG && console.log('📊 Comparing values:', {
        tempCalculatedDuration,
        calculatedDuration,
        percentDiff: percentDiff.toFixed(2) + '%',
        withinThreshold: percentDiff < 10
      });
      
      // Only clear if values are close (within 10%)
      if (percentDiff < 10) {
        DEBUG && console.log('✅ Calculation complete! Clearing temp values. Reporting effect will handle final send.');
        setTempCalculatedDuration(null);
        setTempAverageSpeed(null);
        waitingForCalculation.current = false; // Reset waiting flag
        // Don't send here - let the main reporting effect handle it
      }
    }
  }, [calculatedDuration, isDragging, tempCalculatedDuration]);

  // Report temporary duration during dragging for live updates
  useEffect(() => {
    if (isDragging && tempCalculatedDuration !== null && onDurationCalculated) {
      // During dragging, always show the temporary value regardless of Auto mode
      onDurationCalculated(tempCalculatedDuration);
    }
  }, [isDragging, tempCalculatedDuration, onDurationCalculated]);

  // Shared momentum/acceleration function for both BRouter and Physics calculations
  const applyMomentumToSpeed = (
    rawSpeed: number,
    previousSpeed: number,
    segmentDistance: number
  ): { finalSpeed: number; averageSpeed: number } => {
    let targetSpeed = rawSpeed;
    
    // For short segments, limit speed change (momentum effect)
    if (segmentDistance < 100) {
      const momentumFactor = 0.3; // Only change 30% toward target for short segments
      targetSpeed = previousSpeed + (rawSpeed - previousSpeed) * momentumFactor;
    } else {
      const momentumFactor = 0.7; // Change 70% toward target for longer segments
      targetSpeed = previousSpeed + (rawSpeed - previousSpeed) * momentumFactor;
    }
    
    // Apply realistic speed limits
    targetSpeed = Math.max(5, Math.min(75, targetSpeed)); // Allow up to 75 km/h
    
    // Calculate average speed for the segment
    const averageSpeed = (previousSpeed + targetSpeed) / 2;
    
    return {
      finalSpeed: targetSpeed,
      averageSpeed: averageSpeed
    };
  };

  // Calculate realistic cycling duration based on physics and segment analysis
  const calculateRealisticDuration = (wPerKg: number, route: RouteData, bikeProfile: string = 'road'): { duration: number; averageSpeed: number; chartData: any[]; chartAverageSpeed: number } => {
    const riderWeight = 75; // kg - standard reference
    const basePower = wPerKg * riderWeight; // Base power available (watts)
    
    // Apply smooth calibration curve to make speeds more realistic
    // Balanced calibration - reducing efficiency at higher power to slow down high W/kg
    // Target: 2.08→51:10, 2.56→43:45, 2.74→40:09, 2.97→36:32
    // Feedback: Higher areas too quick, need to slow down
    let powerEfficiency;
    if (wPerKg <= 2.08) {
      // Below 2.08 W/kg: maintain higher efficiency for low power speed
      powerEfficiency = 0.87 - (2.08 - wPerKg) * 0.02; // ~0.85-0.87 (keep as is)
    } else if (wPerKg <= 2.56) {
      // 2.08 to 2.56 W/kg: calibrated for 51:10 → 43:45 
      const t = (wPerKg - 2.08) / (2.56 - 2.08);
      powerEfficiency = 0.87 + t * 0.04; // 0.87 → 0.91 (reduced from 0.93)
    } else if (wPerKg <= 2.74) {
      // 2.56 to 2.74 W/kg: calibrated for 43:45 → 40:09
      const t = (wPerKg - 2.56) / (2.74 - 2.56);
      powerEfficiency = 0.91 + t * 0.04; // 0.91 → 0.95 (reduced from 0.98)
    } else if (wPerKg <= 2.97) {
      // 2.74 to 2.97 W/kg: calibrated for 40:09 → 36:32
      const t = (wPerKg - 2.74) / (2.97 - 2.74);
      powerEfficiency = 0.95 + t * 0.05; // 0.95 → 1.00 (reduced from 1.05)
    } else {
      // Above 2.97 W/kg: slower increase to prevent too quick times
      const excess = wPerKg - 2.97;
      powerEfficiency = 1.00 + excess * 0.02; // slower growth at high power
    }
    const totalPower = basePower * powerEfficiency;
    
    const bike = BIKE_PROFILES[bikeProfile as keyof typeof BIKE_PROFILES] || BIKE_PROFILES.road;
    const totalWeight = riderWeight + bike.weight;
    
    // Use rawSegments for duration calculation (non-aggregated), fallback to segments if not available
    const calculateWithPhysics = (route: RouteData) => {
    
    const segmentsForCalculation = route.rawSegments || route.segments;
    
    if (!segmentsForCalculation || segmentsForCalculation.length === 0) {
      // Fallback to simple calculation if no segments
      return calculateSimpleDuration(wPerKg, route);
    }
    
    let totalTime = 0; // seconds
    let totalDistance = 0; // meters
    const debugSegments: any[] = [];
    const allSegmentsDetails: any[] = []; // New: detailed array for all segments
    const chartData: any[] = []; // Data for speed chart
    
    // Process each segment individually with realistic acceleration/deceleration
    let previousElevation: number | null = null;
    let cumulativeDistance = 0; // For chart x-axis
    let previousSpeed = 20; // Start with reasonable initial speed (km/h)
    
    for (let i = 0; i < segmentsForCalculation.length; i++) {
      const segment = segmentsForCalculation[i];
      const distance = Number(segment.Distance) || 0;
      if (distance <= 0) continue;
      
      const currentElevation = Number(segment.Elevation) || 0;
      const surface = segment.surface || 'paved';
      
      // Calculate gradient properly between consecutive segments
      let gradient = 0;
      if (previousElevation !== null && distance > 0) {
        const elevationChange = currentElevation - previousElevation;
        gradient = (elevationChange / distance) * 100; // Convert to percentage
        
        // Apply smoothing for very short segments to avoid extreme gradients
        if (distance < 20 && Math.abs(gradient) > 10) {
          // For micro-segments, cap the gradient impact
          const smoothingFactor = distance / 20; // 0 to 1 scale
          gradient = gradient * smoothingFactor;
        }
        
        // Sanity check: limit gradients to realistic cycling values
        gradient = Math.max(-20, Math.min(25, gradient)); // Cap at -20% to +25%
      } else if (i > 0 && segmentsForCalculation[i-1]) {
        // Fallback: try to use previous segment elevation if available
        const prevSegment = segmentsForCalculation[i-1];
        const prevElevation = Number(prevSegment.Elevation) || 0;
        if (distance > 0) {
          const elevationChange = currentElevation - prevElevation;
          gradient = (elevationChange / distance) * 100;
          
          // Apply smoothing for very short segments to avoid extreme gradients
          if (distance < 20 && Math.abs(gradient) > 10) {
            const smoothingFactor = distance / 20; // 0 to 1 scale
            gradient = gradient * smoothingFactor;
          }
          
          gradient = Math.max(-20, Math.min(25, gradient)); // Cap at realistic values
        }
      }
      
      // Alternative: Check if BRouter provides gradient directly
      if (segment.gradient !== undefined && segment.gradient !== null) {
        gradient = Number(segment.gradient);
        // Still apply sanity check
        gradient = Math.max(-20, Math.min(25, gradient));
      }
      
      previousElevation = currentElevation;
      
      // Calculate segment time using simplified approach with momentum consideration
      const segmentResult = calculateSegmentTimeSimplified(
        distance,
        gradient,
        surface,
        totalPower,
        totalWeight,
        bike,
        previousSpeed
      );
      
      totalTime += segmentResult.time;
      totalDistance += distance;
      cumulativeDistance += distance;
      previousSpeed = segmentResult.finalSpeed; // Update for next segment
      
      // Add data point for speed chart - ensure all segments are included for unified chart compatibility
      const chartEntry = {
        distance: parseFloat((cumulativeDistance / 1000).toFixed(2)), // km for x-axis as number
        speed: parseFloat(segmentResult.speed.toFixed(1)), // Use average speed during segment
        gradient: parseFloat(gradient.toFixed(1)),
        elevation: parseFloat(currentElevation.toFixed(0)),
        surface: surface,
        segmentDistance: distance // Store segment distance for weighted average calculation
      };
      
      chartData.push(chartEntry);
      
      // Add ALL segments to detailed array
      allSegmentsDetails.push({
        segmentIndex: allSegmentsDetails.length + 1,
        distance: distance,
        elevation: currentElevation.toFixed(1),
        gradient: gradient.toFixed(2),
        surface: surface,
        startSpeed: (i === 0 ? 20 : previousSpeed).toFixed(1), // Show actual start speed
        avgSpeed: segmentResult.speed.toFixed(1),
        endSpeed: segmentResult.finalSpeed.toFixed(1),
        theoreticalSpeed: segmentResult.rawSpeed.toFixed(1),
        timeMinutes: (segmentResult.time / 60).toFixed(2),
        powerUsed: segmentResult.powerUsed.toFixed(0),
        gravityAssist: gradient < -1 ? '⬇️' : gradient > 3 ? '⬆️' : '➡️'
      });
      
      // Collect debug info for significant segments
      if (distance > 100) {
        debugSegments.push({
          distance: distance,
          gradient: gradient,
          surface: surface,
          speed: segmentResult.speed,
          time: segmentResult.time,
          power: segmentResult.powerUsed
        });
      }
    }
    
    // Debug final time calculation before averages
    if (totalTime <= 0 || !isFinite(totalTime)) {
      return { duration: 60, averageSpeed: 15, chartData: [], chartAverageSpeed: 15 }; // Fallback
    }
    
    const avgSpeed = totalDistance > 0 ? (totalDistance / 1000) / (totalTime / 3600) : 0;
    
    // Calculate distance-weighted average speed for chart reference line
    let chartWeightedAvgSpeed = avgSpeed;
    if (chartData.length > 0) {
      const totalChartDistance = chartData.reduce((sum, point) => sum + point.segmentDistance, 0);
      const weightedSpeedSum = chartData.reduce((sum, point) => sum + (point.speed * point.segmentDistance), 0);
      chartWeightedAvgSpeed = totalChartDistance > 0 ? weightedSpeedSum / totalChartDistance : avgSpeed;
    }

    // Use chartData directly - one bar per segment
    return {
      duration: totalTime,
      averageSpeed: avgSpeed,
      chartData: chartData,
      chartAverageSpeed: chartWeightedAvgSpeed
    };
  };
  
  // Simplified calculation that considers momentum but avoids complex acceleration physics
  const calculateSegmentTimeSimplified = (
    distance: number, 
    gradientPercent: number, 
    surface: string, 
    availablePower: number, 
    totalWeight: number, 
    bike: any,
    startSpeed: number
  ): { time: number; speed: number; rawSpeed: number; finalSpeed: number; powerUsed: number } => {
    
    // Get the theoretical optimal speed for this segment
    const theoreticalResult = calculateSegmentTime(
      distance,
      gradientPercent,
      surface,
      availablePower,
      totalWeight,
      bike
    );
    
    const theoreticalSpeed = theoreticalResult.rawSpeed;
    
    // Apply shared momentum logic
    const momentumResult = applyMomentumToSpeed(theoreticalSpeed, startSpeed, distance);
    
    // Calculate time based on average speed
    const segmentTime = (distance / 1000) / momentumResult.averageSpeed * 3600; // seconds
    
    // Ensure realistic time bounds
    const minTime = (distance / 1000) / 35 * 3600; // Max 35 km/h
    const maxTime = (distance / 1000) / 5 * 3600;  // Min 5 km/h
    const actualTime = Math.max(minTime, Math.min(maxTime, segmentTime));
    
    return {
      time: actualTime,
      speed: momentumResult.averageSpeed,
      rawSpeed: theoreticalSpeed,
      finalSpeed: momentumResult.finalSpeed,
      powerUsed: theoreticalResult.powerUsed
    };
  };
  
  // Calculate time for a single segment (original physics-only calculation)
  const calculateSegmentTime = (
    distance: number, 
    gradientPercent: number, 
    surface: string, 
    availablePower: number, 
    totalWeight: number, 
    bike: any
  ): { time: number; speed: number; rawSpeed: number; powerUsed: number } => {
    
    // Get surface-adjusted rolling coefficient
    const surfaceMultiplier = SURFACE_ADJUSTMENTS[surface] || SURFACE_ADJUSTMENTS['unknown'];
    const rollingCoeff = bike.rollingCoeff * surfaceMultiplier;
    
    // Calculate power components for different speeds
    const calculatePowerRequired = (speedKmh: number): { total: number; climbing: number; rolling: number; aero: number } => {
      const speedMs = speedKmh / 3.6;
      
      // Climbing power: P = m * g * sin(θ) * v
      const gradientRadians = Math.atan(gradientPercent / 100);
      const climbingPower = totalWeight * 9.81 * Math.sin(gradientRadians) * speedMs;
      
      // Rolling resistance: P = m * g * Crr * cos(θ) * v
      const rollingPower = totalWeight * 9.81 * rollingCoeff * Math.cos(gradientRadians) * speedMs;
      
      // Aerodynamic resistance: P = 0.5 * ρ * CdA * v³
      // Using standard air density of 1.225 kg/m³
      const aeroPower = 0.5 * 1.225 * bike.cda * Math.pow(speedMs, 3);
      
      const totalRequired = (climbingPower + rollingPower + aeroPower) / bike.efficiency;
      
      return {
        total: totalRequired,
        climbing: climbingPower,
        rolling: rollingPower,
        aero: aeroPower
      };
    };
    
    // Find maximum sustainable speed using binary search
    let minSpeed = 5;  // km/h - minimum cycling speed
    let maxSpeed = 45; // km/h - more realistic maximum speed
    let optimalSpeed = minSpeed;
    
    // Apply gradient-based speed limits - more conservative
    if (gradientPercent > 15) {
      maxSpeed = 10; // Very steep climbs
    } else if (gradientPercent > 10) {
      maxSpeed = 15; // Steep climbs
    } else if (gradientPercent > 6) {
      maxSpeed = 22; // Moderate climbs
    } else if (gradientPercent < -8) {
      maxSpeed = 40; // Fast descents (limited by safety/control)
    }
    
    // Binary search for optimal speed
    const tolerance = 0.1;
    while (maxSpeed - minSpeed > tolerance) {
      const testSpeed = (minSpeed + maxSpeed) / 2;
      const powerRequired = calculatePowerRequired(testSpeed);
      
      // Debug power calculation for climbing
      if (gradientPercent > 3 && distance > 100) {
        // Removed debug logging for production
      }
      
      if (powerRequired.total <= availablePower) {
        optimalSpeed = testSpeed;
        minSpeed = testSpeed;
      } else {
        maxSpeed = testSpeed;
      }
    }
    
    // Enhanced descent handling - gravity assistance is already in the physics
    if (gradientPercent < -1) {
      // On descents, check if we can go faster due to gravity assistance
      // The physics already accounts for negative climbing power (gravity assistance)
      
      // For steep descents, we might be limited by safety rather than power
      let safeDescentSpeed = 75; // Allow high speeds for descents
      
      if (gradientPercent < -15) {
        safeDescentSpeed = 50; // Very steep - more cautious
      } else if (gradientPercent < -10) {
        safeDescentSpeed = 60; // Steep - slightly cautious
      } else if (gradientPercent < -5) {
        safeDescentSpeed = 70; // Moderate descent
      }
      
      // On descents, we might actually reach speeds limited by safety, not power
      // Continue binary search up to safe speed limit
      const descentMaxSpeed = Math.min(safeDescentSpeed, 75); // Cap at 75 km/h max for realistic descents
      
      while (descentMaxSpeed - optimalSpeed > tolerance && optimalSpeed < descentMaxSpeed) {
        const testSpeed = Math.min(optimalSpeed + 2, descentMaxSpeed);
        const powerRequired = calculatePowerRequired(testSpeed);
        
        // On descents, total power requirement might be negative (gravity helps more than needed)
        if (powerRequired.total <= availablePower) {
          optimalSpeed = testSpeed;
        } else {
          break; // Power limited even on descent
        }
      }
      
      // Final safety cap
      optimalSpeed = Math.min(optimalSpeed, safeDescentSpeed);
    }
    
    // Calculate final values
    const finalPower = calculatePowerRequired(optimalSpeed);
    
    // Apply calibration to make results less conservative
    let calibratedSpeed = optimalSpeed;
    
    // Base speed multiplier for real-world performance
    calibratedSpeed *= CALIBRATION_CONFIG.speedMultiplier;
    
    // Power level efficiency bonus (higher W/kg riders are more efficient in practice)
    const riderWeight = totalWeight - bike.weight; // Estimate rider weight
    const estimatedWPerKg = availablePower / riderWeight;
    
    let powerEfficiencyMultiplier = 1.0;
    if (estimatedWPerKg >= 3.0) {
      powerEfficiencyMultiplier = CALIBRATION_CONFIG.powerEfficiencyBonus.high;
    } else if (estimatedWPerKg >= 2.5) {
      powerEfficiencyMultiplier = CALIBRATION_CONFIG.powerEfficiencyBonus.medium;
    } else {
      powerEfficiencyMultiplier = CALIBRATION_CONFIG.powerEfficiencyBonus.low;
    }
    
    calibratedSpeed *= powerEfficiencyMultiplier;
    
    // Terrain-specific adjustments
    let terrainMultiplier = 1.0;
    if (Math.abs(gradientPercent) <= 1.5) {
      terrainMultiplier = CALIBRATION_CONFIG.terrainAdjustments.flat;
    } else if (Math.abs(gradientPercent) <= 4.0) {
      terrainMultiplier = CALIBRATION_CONFIG.terrainAdjustments.rolling;
    } else {
      terrainMultiplier = CALIBRATION_CONFIG.terrainAdjustments.climbing;
    }
    
    calibratedSpeed *= terrainMultiplier;
    
    // Cap at realistic maximum speeds (increased for downhill segments)
    calibratedSpeed = Math.min(calibratedSpeed, 75); // Allow higher speeds for descents (max 75 km/h)
    
    const segmentTime = (distance / 1000) / (calibratedSpeed / 3.6); // Convert km/h to m/s, then calculate time in seconds
    
    // Log descent details for debugging
    if (gradientPercent < -3 && distance > 50) {
      // Removed debug logging for production
    }
    
    return {
      time: segmentTime,
      speed: calibratedSpeed,
      rawSpeed: optimalSpeed, // Store the raw physics speed before calibration
      powerUsed: finalPower.total
    };
  };
  
  // Fallback calculation for routes without detailed segments
  const calculateSimpleDuration = (wPerKg: number, route: RouteData): { duration: number; averageSpeed: number; chartData: any[]; chartAverageSpeed: number } => {
    const distance = route.distance; // meters
    const elevation = route.ascend || 0; // meters
    const avgGradient = distance > 0 ? (elevation / distance) * 100 : 0;
    
    // Estimate average speed based on power and route characteristics
    let estimatedSpeed = 25; // km/h base speed
    
    if (avgGradient > 4) {
      estimatedSpeed = 18; // Hilly routes
    } else if (avgGradient > 2) {
      estimatedSpeed = 22; // Rolling hills
    } else if (avgGradient < 0.5) {
      estimatedSpeed = 28; // Flat routes
    }
    
    // Adjust for power level
    const powerAdjustment = (wPerKg - 2.5) * 2; // ±2 km/h per 0.1 W/kg difference from average
    estimatedSpeed += powerAdjustment;
    
    // Ensure reasonable bounds
    estimatedSpeed = Math.max(12, Math.min(40, estimatedSpeed));
    
    const duration = (distance / 1000) / estimatedSpeed * 3600; // seconds
    
    // Create simple chart data for fallback
    const simpleChartData = [
      { distance: '0.0', speed: estimatedSpeed, gradient: 0, elevation: 0, surface: 'unknown', segmentDistance: distance / 2 },
      { distance: (distance / 1000).toFixed(1), speed: estimatedSpeed, gradient: avgGradient, elevation: elevation, surface: 'unknown', segmentDistance: distance / 2 }
    ];
    
    return {
      duration: duration,
      averageSpeed: estimatedSpeed,
      chartData: simpleChartData,
      chartAverageSpeed: estimatedSpeed
    };
  };

  // Call the physics-based calculation with the route
  return calculateWithPhysics(route);
};

  // Update calculation when inputs change
  useEffect(() => {
    // Skip calculation during dragging to improve performance
    if (isDragging) {
      return;
    }
    
    if (routeData) {
      try {
        setError('');
        
        // Extract BRouter duration for comparison
        const brouterTime = routeData.duration || 0; // BRouter duration in seconds
        const brouterAvgSpeed = brouterTime > 0 ? (routeData.distance / 1000) / (brouterTime / 3600) : 0; // km/h
        
        // Generate unified chart data with both BRouter and Physics speeds
        let unifiedChartData: any[] = [];
        let displayedAverageSpeed = brouterAvgSpeed;
        let displayedDuration = brouterTime;
        
        if (routeData.rawSegments && routeData.rawSegments.length > 0) {
          // Create base chart data with BRouter speed calculations
          let cumulativeDistance = 0;
          let previousElevation: number | null = null;
          let previousBrouterSpeed = 20; // Start with reasonable initial speed for BRouter as well
          let segmentsWithTimeData = 0;
          let segmentsWithoutTimeData = 0;
          let previousCumulativeTime = 0; // Track cumulative time for segment time calculation
          
          unifiedChartData = routeData.rawSegments.map((segment, index) => {
            const segmentDistanceMeters = Number(segment.Distance) || 0;
            if (segmentDistanceMeters <= 0) return null;
            
            cumulativeDistance += segmentDistanceMeters;
            
            // Calculate BRouter speed per segment based on difficulty factors
            const elevation = Number(segment.Elevation) || 0;
            const elevationGain = Math.max(0, (segment.ElevationGain || 0));
            const surfaceType = segment.surface || 'paved';
            
            // Calculate gradient between consecutive segments (same logic as physics calculation)
            let segmentGradient = 0;
            if (previousElevation !== null && segmentDistanceMeters > 0) {
              const elevationChange = elevation - previousElevation;
              segmentGradient = (elevationChange / segmentDistanceMeters) * 100; // Convert to percentage
              
              // Apply smoothing for very short segments to avoid extreme gradients
              if (segmentDistanceMeters < 20 && Math.abs(segmentGradient) > 10) {
                const smoothingFactor = segmentDistanceMeters / 20; // 0 to 1 scale
                segmentGradient = segmentGradient * smoothingFactor;
              }
              
              // Sanity check: limit gradients to realistic cycling values
              segmentGradient = Math.max(-20, Math.min(25, segmentGradient)); // Cap at -20% to +25%
            } else if (index > 0 && routeData.rawSegments && routeData.rawSegments[index - 1]) {
              // Fallback: try to use previous segment elevation if available
              const prevSegment = routeData.rawSegments[index - 1];
              const prevElevation = Number(prevSegment.Elevation) || 0;
              if (segmentDistanceMeters > 0) {
                const elevationChange = elevation - prevElevation;
                segmentGradient = (elevationChange / segmentDistanceMeters) * 100;
                
                // Apply smoothing for very short segments
                if (segmentDistanceMeters < 20 && Math.abs(segmentGradient) > 10) {
                  const smoothingFactor = segmentDistanceMeters / 20;
                  segmentGradient = segmentGradient * smoothingFactor;
                }
                
                segmentGradient = Math.max(-20, Math.min(25, segmentGradient));
              }
            }
            
            // Fallback to elevationGain if we still don't have a gradient
            if (segmentGradient === 0 && elevationGain > 0 && segmentDistanceMeters > 0) {
              segmentGradient = (elevationGain / segmentDistanceMeters) * 100;
              segmentGradient = Math.max(-20, Math.min(25, segmentGradient));
            }
            
            previousElevation = elevation;
            
            // Calculate actual BRouter speed for this segment: distance / time
            let rawBrouterSpeed = 0;
            
            // Try different possible time field names from BRouter (these are cumulative times)
            const cumulativeTime = Number(segment.Time) || Number(segment.Duration) || Number(segment.time) || Number(segment.duration) || 0;
            
            // Calculate the actual segment time by subtracting previous cumulative time
            const segmentTime = cumulativeTime - previousCumulativeTime;
            
            if (segmentTime > 0 && segmentDistanceMeters > 0) {
              // BRouter time is typically in seconds, distance in meters
              // Speed = distance (m) / time (s) * 3.6 to get km/h
              rawBrouterSpeed = (segmentDistanceMeters / segmentTime) * 3.6;
              segmentsWithTimeData++;
              
            } else {
              // Fallback: if no time data available, estimate based on overall average
              segmentsWithoutTimeData++;
              if (index < 10) {
                DEBUG && console.warn(`⚠️ No time data found for segment ${index}, available fields:`, Object.keys(segment));
                DEBUG && console.warn(`⚠️ Segment data:`, segment);
              }
              rawBrouterSpeed = brouterAvgSpeed; // Use overall average as fallback
            }
            
            // Update cumulative time for next iteration
            previousCumulativeTime = cumulativeTime;
            
            // Apply shared momentum logic to BRouter speeds for realistic transitions
            const brouterMomentumResult = applyMomentumToSpeed(rawBrouterSpeed, previousBrouterSpeed, segmentDistanceMeters);
            const finalBrouterSpeed = brouterMomentumResult.averageSpeed;
            
            // Update for next segment
            previousBrouterSpeed = brouterMomentumResult.finalSpeed;
            
            // Ensure reasonable speed bounds
            const boundedBrouterSpeed = Math.max(3, Math.min(60, finalBrouterSpeed));
            
            return {
              distance: parseFloat((cumulativeDistance / 1000).toFixed(2)), // km for x-axis as number (cumulative)
              segmentLength: parseFloat((segmentDistanceMeters / 1000).toFixed(3)), // km - individual segment length
              brouterSpeed: parseFloat(boundedBrouterSpeed.toFixed(1)), // BRouter speed with momentum applied
              physicsSpeed: null, // Will be filled if physics calculation is needed
              elevation: parseFloat(elevation.toFixed(0)),
              gradient: parseFloat(segmentGradient.toFixed(1)), // Use calculated gradient
              surface: surfaceType,
              segmentDistance: segmentDistanceMeters, // Store segment distance in meters for weighted average calculation
              aggregatedCount: 1 // Add this for non-aggregated data
            };
          }).filter(item => item !== null); // Filter out zero-distance segments
          
          // Adaptive aggregation based on total route distance for optimal chart resolution
          const maxDistance = Math.max(...unifiedChartData.map(item => item.distance));
          let distanceInterval: number; // km per chart bar
          
          if (maxDistance < 10) {
            distanceInterval = 0.01; // 10m segments for very short routes
          } else if (maxDistance < 25) {
            distanceInterval = 0.05; // 50m segments for short routes
          } else if (maxDistance < 50) {
            distanceInterval = 0.1; // 100m segments for medium routes
          } else if (maxDistance < 100) {
            distanceInterval = 0.2; // 200m segments for long routes
          } else {
            distanceInterval = 0.25; // 250m segments for very long routes
          }
          
          const targetChartBars = Math.ceil(maxDistance / distanceInterval);
          
          // Always aggregate if we have more than the target number of chart bars OR if segments are very small
          const shouldAggregate = unifiedChartData.length > targetChartBars || 
                                 unifiedChartData.some(item => (item.segmentDistance / 1000) < 0.05); // 50m threshold
          
          if (shouldAggregate && targetChartBars > 1) {
            const aggregatedChartData: any[] = [];
            
            // Create buckets for each distance interval
            for (let distanceMarker = distanceInterval; distanceMarker <= maxDistance + distanceInterval; distanceMarker += distanceInterval) {
              const bucketStart = distanceMarker - distanceInterval;
              const bucketEnd = distanceMarker;
              
              // For each bucket, find segments that intersect with this interval and split them if needed
              const bucketContributions: Array<{
                brouterSpeed: number;
                physicsSpeed: number | null;
                elevation: number;
                gradient: number;
                surface: string;
                distance: number; // meters contributed to this bucket
              }> = [];
              
              for (const segment of unifiedChartData) {
                const segmentStart = segment.distance - (segment.segmentDistance / 1000); // Start position in km
                const segmentEnd = segment.distance; // End position in km
                
                // Check if segment intersects with current bucket
                if (segmentEnd > bucketStart && segmentStart < bucketEnd) {
                  // Calculate the overlap between segment and bucket
                  const overlapStart = Math.max(segmentStart, bucketStart);
                  const overlapEnd = Math.min(segmentEnd, bucketEnd);
                  const overlapDistance = (overlapEnd - overlapStart) * 1000; // Convert to meters
                  
                  if (overlapDistance > 0) {
                    bucketContributions.push({
                      brouterSpeed: segment.brouterSpeed,
                      physicsSpeed: segment.physicsSpeed,
                      elevation: segment.elevation,
                      gradient: segment.gradient,
                      surface: segment.surface,
                      distance: overlapDistance
                    });
                  }
                }
              }
              
              if (bucketContributions.length === 0) continue; // Skip empty buckets
              
              // Calculate weighted averages for the bucket based on distance contributions
              const totalBucketDistance = bucketContributions.reduce((sum, contrib) => sum + contrib.distance, 0);
              
              const weightedBrouterSpeed = bucketContributions.reduce((sum, contrib) => 
                sum + (contrib.brouterSpeed * contrib.distance), 0) / totalBucketDistance;
              
              const weightedPhysicsSpeed = bucketContributions.some(c => c.physicsSpeed !== null) 
                ? bucketContributions.reduce((sum, contrib) => 
                    sum + ((contrib.physicsSpeed || 0) * contrib.distance), 0) / totalBucketDistance
                : null;
              
              const weightedElevation = bucketContributions.reduce((sum, contrib) => 
                sum + (contrib.elevation * contrib.distance), 0) / totalBucketDistance;
              
              const weightedGradient = bucketContributions.reduce((sum, contrib) => 
                sum + (contrib.gradient * contrib.distance), 0) / totalBucketDistance;
              
              // Determine most common surface type by distance
              const surfaceDistances: Record<string, number> = {};
              bucketContributions.forEach(contrib => {
                surfaceDistances[contrib.surface] = (surfaceDistances[contrib.surface] || 0) + contrib.distance;
              });
              const mostCommonSurface = Object.entries(surfaceDistances)
                .reduce((a, b) => a[1] >= b[1] ? a : b)[0];
              
              aggregatedChartData.push({
                distance: parseFloat(distanceMarker.toFixed(3)), // Round distance marker with appropriate precision
                segmentLength: parseFloat((totalBucketDistance / 1000).toFixed(3)), // Total length of aggregated segments
                brouterSpeed: parseFloat(weightedBrouterSpeed.toFixed(1)),
                physicsSpeed: weightedPhysicsSpeed !== null ? parseFloat(weightedPhysicsSpeed.toFixed(1)) : null,
                elevation: parseFloat(weightedElevation.toFixed(0)),
                gradient: parseFloat(weightedGradient.toFixed(1)),
                surface: mostCommonSurface,
                segmentDistance: totalBucketDistance,
                aggregatedCount: bucketContributions.length, // Track how many segment parts were aggregated
                splitSegments: bucketContributions.length > unifiedChartData.filter(s => 
                  s.distance > bucketStart && s.distance <= bucketEnd).length // Flag if splitting occurred
              });
            }
            
            // Replace the detailed data with aggregated data for the chart
            unifiedChartData = aggregatedChartData;
          }
          
          // Validate chart data - check for missing or invalid BRouter speeds
          const invalidSpeeds = unifiedChartData.filter(item => 
            item.brouterSpeed === null || 
            item.brouterSpeed === undefined || 
            isNaN(item.brouterSpeed) || 
            item.brouterSpeed <= 0
          );
          
          if (invalidSpeeds.length > 0) {
            DEBUG && console.warn(`⚠️ Found ${invalidSpeeds.length} segments with invalid BRouter speeds:`, 
              invalidSpeeds.slice(0, 5).map(item => ({
                distance: item.distance,
                brouterSpeed: item.brouterSpeed,
                gradient: item.gradient
              }))
            );
          }

        }
        
        // Always calculate physics for the hidden calculator (needed for state even in Auto mode)
        // For display-only mode, only calculate if not in Auto mode
        if (effectivePowerToWeight > 0 && (!displayOnly || !effectiveShowOriginal)) {
          // Calculate physics-based speeds using ORIGINAL route data, not aggregated chart data
          const physicsResult = calculateRealisticDuration(effectivePowerToWeight, routeData);
          
          // Store the original physics result
          displayedDuration = physicsResult.duration;
          displayedAverageSpeed = physicsResult.averageSpeed;
          setChartAverageSpeed(physicsResult.chartAverageSpeed);
          
          // If we have aggregated chart data, merge physics speeds into it
          if (unifiedChartData.length > 0) {
            // For aggregated data, we need to recalculate physics speeds based on the aggregation
            if (unifiedChartData.some(item => item.aggregatedCount > 1)) {
           
              // Map original physics data to aggregated chart segments
              const originalPhysicsData = physicsResult.chartData;
              
              unifiedChartData.forEach((chartItem, chartIndex) => {
                // Find all original physics segments that contribute to this aggregated chart bar
                const chartBarStart = chartItem.distance - (chartItem.segmentDistance / 1000);
                const chartBarEnd = chartItem.distance;
                
                let weightedPhysicsSpeed = 0;
                let totalContributingDistance = 0;
                
                // Look through original physics data to find segments that overlap with this chart bar
                originalPhysicsData.forEach((physicsItem, physicsIndex) => {
                  const physicsBarStart = physicsIndex === 0 ? 0 : originalPhysicsData[physicsIndex - 1]?.distance || 0;
                  const physicsBarEnd = physicsItem.distance;
                  
                  // Check for overlap between physics segment and chart bar
                  if (physicsBarEnd > chartBarStart && physicsBarStart < chartBarEnd) {
                    const overlapStart = Math.max(physicsBarStart, chartBarStart);
                    const overlapEnd = Math.min(physicsBarEnd, chartBarEnd);
                    const overlapDistance = overlapEnd - overlapStart;
                    
                    if (overlapDistance > 0) {
                      weightedPhysicsSpeed += physicsItem.speed * overlapDistance;
                      totalContributingDistance += overlapDistance;
                    }
                  }
                });
                
                // Set the weighted average physics speed
                if (totalContributingDistance > 0) {
                  chartItem.physicsSpeed = parseFloat((weightedPhysicsSpeed / totalContributingDistance).toFixed(1));
                } else {
                  // Fallback: use closest physics speed
                  const closestPhysicsIndex = Math.min(chartIndex, originalPhysicsData.length - 1);
                  chartItem.physicsSpeed = originalPhysicsData[closestPhysicsIndex]?.speed || 20;
                }
              });
            } else {
              // Data was not aggregated - direct mapping
              unifiedChartData.forEach((chartItem, index) => {
                if (index < physicsResult.chartData.length) {
                  chartItem.physicsSpeed = physicsResult.chartData[index].speed;
                }
              });
            }
          }
          
        } else {
          // BRouter mode - set display values
          displayedDuration = brouterTime;
          displayedAverageSpeed = brouterAvgSpeed;
          setChartAverageSpeed(brouterAvgSpeed);
        }
        
        // Set the final values
        DEBUG && console.log('⚙️ Calculation complete, setting calculatedDuration to:', displayedDuration);
        setCalculatedDuration(displayedDuration);
        setAverageSpeed(displayedAverageSpeed);
        setSpeedChartData(unifiedChartData);
        
      } catch (_err) {
        // Error handled silently for production
        setError('Fehler bei der Berechnung');
        setCalculatedDuration(null);
        setAverageSpeed(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeData, effectivePowerToWeight, effectiveShowOriginal]); // Use effective values for controlled mode

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes} min`;
    }
    return `${minutes} min`;
  };

  // If headless mode, render nothing but still run all calculations
  if (headless) {
    return null;
  }

  return (
    <Box className="power-duration-calculator">
      {calculatedDuration && (
        <>
          {/* Top: Results Section - Full Width */}
          <Box className="pdc-top-row">
            <Box className="pdc-results-section">
              {/* Duration Box */}
              <Box className="pdc-results-content">
                <Box className="pdc-results-header">
                  <AccessTimeIcon sx={{ fontSize: '24px' }} />
                  <Typography variant="h6" className="pdc-results-title">
                    Duration
                  </Typography>
                </Box>
                <Typography variant="h6" className="pdc-results-value">
                  {formatDuration(isDragging && tempCalculatedDuration ? tempCalculatedDuration : calculatedDuration)}
                </Typography>
              </Box>

              {/* Speed Box */}
              <Box className="pdc-results-content">
                <Box className="pdc-results-header">
                  <SpeedIcon sx={{ fontSize: '24px' }} />
                  <Typography variant="h6" className="pdc-results-title">
                    Avg Speed
                  </Typography>
                </Box>
                <Typography variant="h6" className="pdc-results-value">
                  {((isDragging && tempAverageSpeed ? tempAverageSpeed : averageSpeed) || 0).toFixed(1)} km/h
                </Typography>
              </Box>
            </Box>

            {/* Controls Section - Full Width Slider Layout */}
            <Box className="pdc-controls-section">
              {/* Auto button */}
              <Box className="pdc-auto-container">
                <Chip
                  label={
                    <Box className="pdc-auto-label-container">
                      <TipsAndUpdatesOutlinedIcon sx={{ fontSize: '16px' }} />
                      <Box className="pdc-auto-label-text">
                        Auto
                      </Box>
                    </Box>
                  }
                  onClick={() => {
                    // Toggle auto mode on/off
                    if (showOriginal) {
                      // Turning off auto mode - switch to manual
                      setShowOriginal(false);
                      setIsCustomMode(true);
                    } else {
                      // Turning on auto mode
                      setShowOriginal(true);
                      setIsCustomMode(false);
                      setPowerToWeight(2.5);
                      setTempPowerToWeight(2.5);
                    }
                  }}
                  variant={showOriginal ? 'filled' : 'outlined'}
                  size="small"
                  className="pdc-auto-chip"
                />
              </Box>

              {/* Slider container */}
              <Box className={`pdc-slider-container ${showOriginal ? 'auto-mode' : ''}`}>
                <Box className="pdc-slider-track">
                  {(() => {
                    // Calculate values once to avoid repeated calculations during render
                    const currentValue = isDragging ? tempPowerToWeight : powerToWeight;
                    const isOutOfRange = currentValue < 1.0 || currentValue > 4.0;
                    
                    return (
                      <input
                        type="range"
                        min="1.0"
                        max="4.0"
                        step="0.05"
                        value={currentValue}
                        onMouseDown={() => {
                          // Automatically disable Auto mode when starting to drag
                          if (showOriginal) {
                            setShowOriginal(false);
                            setIsCustomMode(true);
                          }
                          setIsDragging(true);
                          setTempPowerToWeight(powerToWeight);
                        }}
                        onTouchStart={() => {
                          // Automatically disable Auto mode when starting to drag
                          if (showOriginal) {
                            setShowOriginal(false);
                            setIsCustomMode(true);
                          }
                          setIsDragging(true);
                          setTempPowerToWeight(powerToWeight);
                        }}
                        onChange={(e) => {
                          const newValue = parseFloat(e.target.value);
                          if (isDragging) {
                            // Update temporary value and calculate with the same logic used on release
                            requestAnimationFrame(() => {
                              setTempPowerToWeight(newValue);
                              // Use the same calculation as the main useEffect
                              if (routeData) {
                                try {
                                  let tempResult;
                                  if (showOriginal) {
                                    // If showing original, use BRouter values
                                    const brouterTime = routeData.duration || 0;
                                    const brouterAvgSpeed = brouterTime > 0 ? (routeData.distance / 1000) / (brouterTime / 3600) : 0;
                                    tempResult = { duration: brouterTime, averageSpeed: brouterAvgSpeed };
                                  } else {
                                    // Use the same physics calculation as the main logic
                                    tempResult = calculateRealisticDuration(newValue, routeData, 'road');
                                  }
                                  setTempCalculatedDuration(tempResult.duration);
                                  setTempAverageSpeed(tempResult.averageSpeed);
                                } catch (_error) {
                                  // On error, keep previous temp values
                                }
                              }
                            });
                          } else {
                            // Direct update when not dragging (e.g., programmatic changes)
                            setShowOriginal(false);
                            setIsCustomMode(true);
                            setPowerToWeight(newValue);
                          }
                        }}
                        onMouseUp={() => {
                          if (isDragging) {
                            DEBUG && console.log('🖱️ Mouse up! Setting waiting flag and applying temp value:', tempPowerToWeight);
                            // Apply the temporary value and trigger recalculation
                            waitingForCalculation.current = true; // Set flag to prevent premature duration updates
                            setPowerToWeight(tempPowerToWeight);
                            setIsDragging(false);
                            // Temp values will be cleared automatically when real calculation completes
                          }
                        }}
                        onTouchEnd={() => {
                          if (isDragging) {
                            DEBUG && console.log('👆 Touch end! Setting waiting flag and applying temp value:', tempPowerToWeight);
                            // Apply the temporary value and trigger recalculation (for mobile)
                            waitingForCalculation.current = true; // Set flag to prevent premature duration updates
                            setPowerToWeight(tempPowerToWeight);
                            setIsDragging(false);
                            // Temp values will be cleared automatically when real calculation completes
                          }
                        }}
                        className={`pdc-slider ${isOutOfRange ? 'out-of-range' : ''}`}
                        disabled={isOutOfRange}
                      />
                    );
                  })()}
                  
                </Box>
                
                <Box className="pdc-slider-labels">
                  {[1.5, 2.0, 2.5, 3.0, 3.5].map((value) => {
                    const position = ((value - 1.0) / (4.0 - 1.0)) * 100;
                    return (
                      <span 
                        key={value}
                        style={{ left: `${position}%` }}
                      >
                        {value.toFixed(1)}
                      </span>
                    );
                  })}
                </Box>
              </Box>

              {/* Power input field */}
              <Box className="pdc-power-input-container">
                <TextField
                  type="number"
                  value={showOriginal ? '' : (isDragging ? tempPowerToWeight : powerToWeight)}
                  onChange={(e) => {
                    setShowOriginal(false);
                    setIsCustomMode(true);
                    const newValue = Number(e.target.value);
                    setPowerToWeight(newValue);
                    setTempPowerToWeight(newValue);
                  }}
                  inputProps={{
                    min: 1.0,
                    max: 6.0,
                    step: 0.1,
                  }}
                  size="small"
                  error={!!error}
                  disabled={showOriginal}
                  className="pdc-power-input"
                  placeholder={showOriginal ? "Auto" : "W/kg"}
                />
                <span className="pdc-power-label">W/kg</span>
              </Box>
            </Box>
          </Box>

          {/* Bottom: Full-Width Chart Section */}
          {speedChartData.length > 0 && (
            <Box className="pdc-chart-section">
              
              <Box className="pdc-chart-container">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={speedChartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid 
                      strokeDasharray="3 3" 
                      stroke={getChartColors().grid} 
                      style={{ opacity: 0.3 }}
                    />
                    <XAxis 
                      dataKey="distance" 
                      tick={{ fontSize: 10, fill: getChartColors().text }}
                      axisLine={{ stroke: getChartColors().axis }}
                      tickLine={{ stroke: getChartColors().axis }}
                      interval="preserveStartEnd"
                      tickFormatter={(value) => `${Math.round(parseFloat(value))}km`}
                      label={{ value: 'km', position: 'insideBottom', offset: -3, style: { textAnchor: 'middle', fontSize: '10px', fill: getChartColors().text } }}
                    />
                    <YAxis 
                      tick={{ fontSize: 10, fill: getChartColors().text }}
                      axisLine={{ stroke: getChartColors().axis }}
                      tickLine={{ stroke: getChartColors().axis }}
                      width={40}
                      tickFormatter={(value) => `${value}`}
                      label={{ value: 'km/h', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: '10px', fill: getChartColors().text } }}
                    />
                    <RechartsTooltip 
                      content={({ active, payload }: any) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          const displaySpeed = showOriginal ? data.brouterSpeed : data.physicsSpeed;
                          return (
                            <Box className="pdc-tooltip">
                              <Typography variant="caption" className="pdc-tooltip-title">
                                {data.distance} km (total)
                              </Typography>
                              <Typography variant="caption" className="pdc-tooltip-segment">
                                📏 {(data.segmentDistance / 1000).toFixed(3)} km (segment{(data.aggregatedCount && data.aggregatedCount > 1) ? 's' : ''})
                              </Typography>
                              {data.aggregatedCount && data.aggregatedCount > 1 && (
                                <Typography variant="caption" className="pdc-tooltip-segment">
                                  🔢 {data.aggregatedCount} segments aggregated
                                </Typography>
                              )}
                              <Typography variant="caption" className="pdc-tooltip-speed">
                                🏃‍♂️ {displaySpeed} km/h {showOriginal ? '(BRouter)' : '(Physics)'}
                              </Typography>
                              <Typography variant="caption" className="pdc-tooltip-gradient">
                                📈 {data.gradient}% (avg)
                              </Typography>
                              <Typography variant="caption" className="pdc-tooltip-elevation">
                                ⛰️ {data.elevation}m (avg)
                              </Typography>
                              <Typography variant="caption" className="pdc-tooltip-surface">
                                🛣️ {data.surface} (primary)
                              </Typography>
                            </Box>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar 
                      dataKey={showOriginal ? "brouterSpeed" : "physicsSpeed"} 
                      fill={getChartColors().primary}
                      opacity={0.8}
                      radius={[0, 0, 0, 0]}
                      stroke={getChartColors().primary}
                      strokeWidth={1}
                    />
                    <ReferenceLine 
                      y={chartAverageSpeed || 0} 
                      stroke={getChartColors().primary} 
                      strokeWidth={2}
                      strokeDasharray="none"
                      label={{ 
                        value: `⌀ ${(chartAverageSpeed || 0).toFixed(1)}`, 
                        position: 'right', 
                        style: { fontSize: '10px', fill: getChartColors().text } 
                      }}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          )}
        </>
      )}

      {/* Helper text */}
      {error && (
        <Typography variant="caption" className="pdc-error-text">
          {error}
        </Typography>
      )}
      <Typography variant="caption" className="pdc-helper-text">
        Typcial Values: Beginner 2.0, Hobby 2.5, Sporty 3.0
      </Typography>
      
      <Typography variant="caption" className="pdc-description-text">
        Based on physics simulation with gradient, surface and resistances.
      </Typography>
    </Box>
  );
};
